import {
  DEFAULT_TRAUMA_INTRO_QUESTION_DESCRIPTION,
  DEFAULT_TRAUMA_INTRO_QUESTION_TITLE,
  DEFAULT_TRAUMA_NAVIGATION_TITLE,
  DEFAULT_TRAUMA_PRODUCT_DESCRIPTION_PARAGRAPHS,
  DEFAULT_TRAUMA_PRODUCT_TITLE,
  DEFAULT_TRAUMA_REFERENCE_NUMBER_LABEL,
} from '@config/traumaCompanyConfigDefaults';

export const DEFAULT_TRAUMA_CHECKIN_INTERVAL_DAYS = [28, 90, 180, 365] as const;

const normalizeTraumaConfigString = (value: string | null | undefined): string | null => {
  if (value == null || value === 'null' || value.trim() === '') {
    return null;
  }

  return value;
};

export const parseTraumaCheckinIntervalsDays = (raw: string | null | undefined): number[] => {
  if (!raw || raw.trim() === '' || raw.trim() === '{}') {
    return [...DEFAULT_TRAUMA_CHECKIN_INTERVAL_DAYS];
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...DEFAULT_TRAUMA_CHECKIN_INTERVAL_DAYS];
    }

    const days = parsed.filter((entry): entry is number => typeof entry === 'number' && entry > 0);

    return days.length > 0 ? days : [...DEFAULT_TRAUMA_CHECKIN_INTERVAL_DAYS];
  } catch {
    return [...DEFAULT_TRAUMA_CHECKIN_INTERVAL_DAYS];
  }
};

export const normalizeTraumaCompanyConfigStrings = <
  T extends {
    traumaNavigationTitle?: string | null;
    traumaProductTitle?: string | null;
    traumaProductDescription?: string | null;
    traumaIntroQuestionTitle?: string | null;
    traumaIntroQuestionDescription?: string | null;
    traumaReferenceNumberLabel?: string | null;
    traumaCheckinIntervalsDays?: string | null;
  },
>(
  data: T,
): T => ({
  ...data,
  traumaNavigationTitle: normalizeTraumaConfigString(data.traumaNavigationTitle),
  traumaProductTitle: normalizeTraumaConfigString(data.traumaProductTitle),
  traumaProductDescription: normalizeTraumaConfigString(data.traumaProductDescription),
  traumaIntroQuestionTitle: normalizeTraumaConfigString(data.traumaIntroQuestionTitle),
  traumaIntroQuestionDescription: normalizeTraumaConfigString(data.traumaIntroQuestionDescription),
  traumaReferenceNumberLabel: normalizeTraumaConfigString(data.traumaReferenceNumberLabel),
  traumaCheckinIntervalsDays: normalizeTraumaConfigString(data.traumaCheckinIntervalsDays),
});

const resolveTraumaConfigString = (value: string | null | undefined, fallback: string): string => {
  const normalized = normalizeTraumaConfigString(value);

  return normalized ?? fallback;
};

export const resolveTraumaNavigationTitle = (value: string | null | undefined): string =>
  resolveTraumaConfigString(value, DEFAULT_TRAUMA_NAVIGATION_TITLE);

export const resolveTraumaProductTitle = (value: string | null | undefined): string =>
  resolveTraumaConfigString(value, DEFAULT_TRAUMA_PRODUCT_TITLE);

export const resolveTraumaIntroQuestionTitle = (value: string | null | undefined): string =>
  resolveTraumaConfigString(value, DEFAULT_TRAUMA_INTRO_QUESTION_TITLE);

export const resolveTraumaIntroQuestionDescription = (value: string | null | undefined): string =>
  resolveTraumaConfigString(value, DEFAULT_TRAUMA_INTRO_QUESTION_DESCRIPTION);

export const resolveTraumaReferenceNumberLabel = (value: string | null | undefined): string =>
  resolveTraumaConfigString(value, DEFAULT_TRAUMA_REFERENCE_NUMBER_LABEL);

export const resolveTraumaMenuLabel = (itemName: string, navigationTitle: string): string =>
  itemName === 'Trauma' ? navigationTitle : itemName;

export const parseTraumaProductDescriptionParagraphs = (
  raw: string | null | undefined,
): string[] => {
  const normalized = normalizeTraumaConfigString(raw);

  if (!normalized) {
    return [...DEFAULT_TRAUMA_PRODUCT_DESCRIPTION_PARAGRAPHS];
  }

  const paragraphs = normalized
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return paragraphs.length > 0 ? paragraphs : [...DEFAULT_TRAUMA_PRODUCT_DESCRIPTION_PARAGRAPHS];
};
